import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { useDispatch } from "react-redux";
import { FormikSaveBar } from "../../../utils/FormikSaveBar";
import { PMField } from "../../../utils/PMField";
import PMSwitch from "../../../utils/PMSwitch";
import { updateProjectFolderAsync } from "../../../../slices/project/folder";
import { FolderIcon, DocumentTextIcon } from "@heroicons/react/24/outline";

export const FolderInfo = (props) => {
  const { t } = useTranslation();
  const { item } = props;
  const dispatch = useDispatch();

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FolderIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {item?.title || "Mappe Detaljer"}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Administrer mappeinnstillinger og detaljer
              </p>
            </div>
          </div>
        </div>

      <Formik
        enableReinitialize
        initialValues={{
          id: item?.id,
          title: item?.title,
          description: item?.description,
          submit: null,
          active: item?.active || false,
        }}
        validationSchema={Yup.object({
          title: Yup.string().max(255).required("Tittel er påkrevd"),
          description: Yup.string(),
        })}
        onSubmit={(values, helpers) => {
          try {
            toast.promise(dispatch(updateProjectFolderAsync(values)), {
              loading: t("Lagrer..."),
              success: (data) => {
                if (data.status === 500) throw new Error("Server error");
                return t("Data ble lagret!");
              },
              error: t("Noe gikk galt. Kunne ikke lagre data!"),
            });
            helpers.setStatus({ success: true });
            helpers.setSubmitting(false);
          } catch (err) {
            toast.error("Feil ved lagring");
            console.error(err);
            helpers.setStatus({ success: false });
            helpers.setErrors({ submit: err.message });
            helpers.setSubmitting(false);
          }
        }}
      >
        {(formik) => (
          <Form className="space-y-6">
            {/* Status Section */}
            <div className="bg-white shadow-sm rounded-xl border border-gray-200">
              <div className="px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <div className={`h-3 w-3 rounded-full ${formik.values.active ? 'bg-green-500' : 'bg-gray-300'}`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Status</h3>
                      <p className="text-sm text-gray-500">
                        {formik.values.active ? 'Aktiv mappe' : 'Inaktiv mappe'}
                      </p>
                    </div>
                  </div>
                  <PMSwitch label="Aktiv" name="active" {...formik} />
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div className="bg-white shadow-sm rounded-xl border border-gray-200">
              <div className="px-6 py-5">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Detaljer</h3>
                    <p className="text-sm text-gray-500">
                      Grunnleggende informasjon om mappen
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <PMField
                      name="title"
                      type="text"
                      label="Tittel"
                      placeholder="Skriv inn mappetittel..."
                      {...formik}
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <PMField
                      name="description"
                      type="text"
                      label="Beskrivelse"
                      placeholder="Beskriv mappens innhold eller formål..."
                      multiline
                      rows={4}
                      {...formik}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Save Bar */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 -mx-4 sm:-mx-6 lg:-mx-8">
              <FormikSaveBar {...formik} />
            </div>
          </Form>
        )}
      </Formik>
      </div>
    </div>
  );
};
